import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import localUtil from '../../lib/util';

const WEEKDAYS = [
    { key: 0, short: 'Sun', label: 'Sunday' },
    { key: 1, short: 'Mon', label: 'Monday' },
    { key: 2, short: 'Tue', label: 'Tuesday' },
    { key: 3, short: 'Wed', label: 'Wednesday' },
    { key: 4, short: 'Thu', label: 'Thursday' },
    { key: 5, short: 'Fri', label: 'Friday' },
    { key: 6, short: 'Sat', label: 'Saturday' },
];

@observer
class BulkGenerateConventions extends Component {
    constructor(props) {
        super(props);
        const { conventionStore } = props.stores;
        const { convention } = conventionStore;

        // Default start time to convention start date or now
        const defaultStartDate = convention.startDate
            ? util.isoDate(convention.startDate)
            : util.isoDate(new Date());

        // Default end time to convention end date or start + 2 hours
        const defaultEndDate = convention.endDate
            ? util.isoDate(convention.endDate)
            : util.isoDate(new Date(new Date(convention.startDate || new Date()).getTime() + 2 * 60 * 60 * 1000));

        // Extract time from convention for repeat pattern defaults
        const startDateObj = new Date(convention.startDate || new Date());
        const endDateObj = new Date(convention.endDate || new Date(startDateObj.getTime() + 2 * 60 * 60 * 1000));
        const defaultStartTime = `${String(startDateObj.getHours()).padStart(2, '0')}:${String(startDateObj.getMinutes()).padStart(2, '0')}`;
        const defaultEndTime = `${String(endDateObj.getHours()).padStart(2, '0')}:${String(endDateObj.getMinutes()).padStart(2, '0')}`;

        // Default repeat range: today to 3 months from now
        const today = new Date();
        const threeMonthsLater = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);

        this.state = {
            conventions: [
                { startDate: defaultStartDate, endDate: defaultEndDate },
            ],
            loading: false,
            error: null,
            // Repeat pattern state
            repeatDays: [], // Array of weekday numbers (0=Sun, 1=Mon, etc.)
            repeatFromDate: util.isoDate(today, false, false, true), // Date only
            repeatToDate: util.isoDate(threeMonthsLater, false, false, true), // Date only
            repeatStartTime: defaultStartTime,
            repeatEndTime: defaultEndTime,
        };
    }

    handleStartDateChange = (index, e) => {
        const { conventions } = this.state;
        const newConventions = [...conventions];
        newConventions[index] = { ...newConventions[index], startDate: e.target.value };
        this.setState({ conventions: newConventions, error: null });
    }

    handleEndDateChange = (index, e) => {
        const { conventions } = this.state;
        const newConventions = [...conventions];
        newConventions[index] = { ...newConventions[index], endDate: e.target.value };
        this.setState({ conventions: newConventions, error: null });
    }

    addDateRow = () => {
        const { conventions } = this.state;
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;

        // Default to convention dates or empty
        const defaultStartDate = convention.startDate
            ? util.isoDate(convention.startDate)
            : util.isoDate(new Date());
        const defaultEndDate = convention.endDate
            ? util.isoDate(convention.endDate)
            : util.isoDate(new Date(new Date(convention.startDate || new Date()).getTime() + 2 * 60 * 60 * 1000));

        this.setState({
            conventions: [...conventions, { startDate: defaultStartDate, endDate: defaultEndDate }],
            error: null,
        });
    }

    removeDateRow = (index) => {
        const { conventions } = this.state;
        if (conventions.length > 1) {
            const newConventions = conventions.filter((_, i) => i !== index);
            this.setState({ conventions: newConventions, error: null });
        }
    }

    clearAllDates = () => {
        this.setState({ conventions: [], error: null });
    }

    // Repeat pattern handlers
    toggleRepeatDay = (dayKey) => {
        const { repeatDays } = this.state;
        if (repeatDays.includes(dayKey)) {
            this.setState({ repeatDays: repeatDays.filter(d => d !== dayKey) });
        } else {
            this.setState({ repeatDays: [...repeatDays, dayKey].sort((a, b) => a - b) });
        }
    }

    handleRepeatFromDateChange = (e) => {
        this.setState({ repeatFromDate: e.target.value });
    }

    handleRepeatToDateChange = (e) => {
        this.setState({ repeatToDate: e.target.value });
    }

    handleRepeatStartTimeChange = (e) => {
        this.setState({ repeatStartTime: e.target.value });
    }

    handleRepeatEndTimeChange = (e) => {
        this.setState({ repeatEndTime: e.target.value });
    }

    generateFromPattern = () => {
        const { repeatDays, repeatFromDate, repeatToDate, repeatStartTime, repeatEndTime, conventions } = this.state;

        if (repeatDays.length === 0) {
            this.setState({ error: 'Please select at least one weekday' });
            return;
        }

        if (!repeatFromDate || !repeatToDate) {
            this.setState({ error: 'Please select from and to dates' });
            return;
        }

        if (!repeatStartTime || !repeatEndTime) {
            this.setState({ error: 'Please select start and end times' });
            return;
        }

        const fromDate = new Date(repeatFromDate);
        const toDate = new Date(repeatToDate);

        if (toDate < fromDate) {
            this.setState({ error: 'To date must be after from date' });
            return;
        }

        // Generate dates
        const newDates = [];
        const currentDate = new Date(fromDate);

        while (currentDate <= toDate) {
            const dayOfWeek = currentDate.getDay();
            if (repeatDays.includes(dayOfWeek)) {
                // Create start datetime
                const dateStr = util.isoDate(currentDate, false, false, true);
                const startDateTime = `${dateStr}T${repeatStartTime}`;
                const endDateTime = `${dateStr}T${repeatEndTime}`;

                newDates.push({
                    startDate: startDateTime,
                    endDate: endDateTime,
                });
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        if (newDates.length === 0) {
            this.setState({ error: 'No dates match the selected pattern' });
            return;
        }

        // Filter out empty existing conventions and add new ones
        const existingValid = conventions.filter(c => c.startDate && c.endDate);
        this.setState({
            conventions: [...existingValid, ...newDates],
            error: null,
        });
    }

    handleGenerate = async () => {
        const { conventionStore, appState } = this.props.stores;
        const { convention } = conventionStore;
        const { callback } = this.props;
        const { conventions } = this.state;

        // Validate all dates
        const validConventions = conventions.filter(c => c.startDate && c.endDate);
        if (validConventions.length === 0) {
            this.setState({ error: 'At least one convention with start and end date is required' });
            return;
        }

        this.setState({ loading: true, error: null });

        try {
            const response = await conventionStore.bulkGenerateConventions({
                sourceConventionId: convention.id,
                conventions: validConventions,
            });

            if (response.status === 201) {
                appState.addToast({
                    type: 'success',
                    message: `Successfully created ${validConventions.length} conventions`,
                }, 5000);

                // Close drawer and refresh
                if (callback) {
                    await callback();
                }
                appState.closeDrawer();
            } else {
                throw new Error(response.message || 'Failed to create conventions');
            }
        } catch (err) {
            this.setState({ error: err.message });
            appState.addToast({
                type: 'error',
                message: `Failed to create conventions: ${err.message}`,
            }, 5000);
        } finally {
            this.setState({ loading: false });
        }
    }

    render() {
        const {
            conventions,
            loading,
            error,
            repeatDays,
            repeatFromDate,
            repeatToDate,
            repeatStartTime,
            repeatEndTime,
        } = this.state;
        const { appState, userStore, conventionStore } = this.props.stores;
        const { user = {} } = userStore;
        const { convention } = conventionStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const membersCount = convention.members?.length || 0;
        const validCount = conventions.filter(c => c.startDate && c.endDate).length;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class={`font-weight-lighter px-3 mt-3 box-header w-100 position-relative`}>
                        <Text id='convention.bulk-generate-conventions'>Copy Convention to Multiple Dates</Text>
                    </div>

                    <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        {/* Source convention info */}
                        <div class='d-flex flex-column'>
                            <span class='text-muted font-weight-light'>
                                <Text id='convention.source-convention'>Source Convention</Text>
                            </span>
                            <span class='font-weight-bold'>{convention.title}</span>
                            <div class='d-flex flex-wrap mt-2'>
                                <small class='badge bg-secondary me-2 mb-1'>
                                    <Text id='convention.type'>Type</Text>: {convention.type || 'N/A'}
                                </small>
                                <small class='badge bg-secondary me-2 mb-1'>
                                    <Text id='convention.stage-interval'>Stage interval</Text>: {convention.stageInterval || 'N/A'} min
                                </small>
                                <small class='badge bg-secondary me-2 mb-1'>
                                    <Text id='convention.stage-slots'>Stage slots</Text>: {convention.stageSlots || 'N/A'}
                                </small>
                                <small class='badge bg-secondary me-2 mb-1'>
                                    <Text id='convention.admins'>Admins</Text>: {membersCount}
                                </small>
                            </div>
                        </div>

                        {/* Info text */}
                        <div class='alert alert-info mt-3 rounded-lg'>
                            <i class='fa-duotone fa-info-circle me-2' />
                            <Text id='convention.bulk-generate-info'>
                                Title will be copied from source. Stages will be empty - use "Bulk Generate Stages" on each new convention.
                            </Text>
                        </div>

                        {/* Repeat pattern section */}
                        <div class='d-flex flex-column mt-4 p-3 border rounded-lg'>
                            <span class='text-muted font-weight-light mb-2'>
                                <i class='fa-duotone fa-repeat me-2' />
                                <Text id='convention.repeat-pattern'>Repeat pattern</Text>
                            </span>

                            {/* Weekday selection */}
                            <div class='d-flex flex-wrap mb-3'>
                                {WEEKDAYS.map(day => (
                                    <button
                                        key={day.key}
                                        type='button'
                                        class={`btn btn-sm me-1 mb-1 rounded-pill ${repeatDays.includes(day.key) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => this.toggleRepeatDay(day.key)}
                                    >
                                        {day.short}
                                    </button>
                                ))}
                            </div>

                            {/* Date range */}
                            <div class='d-flex flex-row flex-wrap mb-3'>
                                <div class='me-3 mb-2'>
                                    <label class='text-muted small'>
                                        <Text id='convention.from-date'>From date</Text>
                                    </label>
                                    <input
                                        class='form-control form-control-sm rounded-pill'
                                        type='date'
                                        value={repeatFromDate}
                                        onInput={this.handleRepeatFromDateChange}
                                    />
                                </div>
                                <div class='me-3 mb-2'>
                                    <label class='text-muted small'>
                                        <Text id='convention.to-date'>To date</Text>
                                    </label>
                                    <input
                                        class='form-control form-control-sm rounded-pill'
                                        type='date'
                                        value={repeatToDate}
                                        onInput={this.handleRepeatToDateChange}
                                    />
                                </div>
                            </div>

                            {/* Time range */}
                            <div class='d-flex flex-row flex-wrap mb-3'>
                                <div class='me-3 mb-2'>
                                    <label class='text-muted small'>
                                        <Text id='convention.start-time'>Start time</Text>
                                    </label>
                                    <input
                                        class='form-control form-control-sm rounded-pill'
                                        type='time'
                                        value={repeatStartTime}
                                        onInput={this.handleRepeatStartTimeChange}
                                    />
                                </div>
                                <div class='me-3 mb-2'>
                                    <label class='text-muted small'>
                                        <Text id='convention.end-time'>End time</Text>
                                    </label>
                                    <input
                                        class='form-control form-control-sm rounded-pill'
                                        type='time'
                                        value={repeatEndTime}
                                        onInput={this.handleRepeatEndTimeChange}
                                    />
                                </div>
                            </div>

                            {/* Generate button */}
                            <button
                                class='btn btn-outline-success rounded-pill'
                                onClick={this.generateFromPattern}
                                disabled={repeatDays.length === 0}
                            >
                                <i class='fa-duotone fa-calendar-plus me-2' />
                                <Text id='convention.generate-dates'>Generate dates from pattern</Text>
                            </button>
                        </div>

                        {/* Date rows */}
                        <div class='d-flex flex-column mt-4'>
                            <div class='d-flex justify-content-between align-items-center mb-2'>
                                <span class='text-muted font-weight-light'>
                                    <Text id='convention.dates-for-new-conventions'>Dates for new conventions</Text>
                                </span>
                                {conventions.length > 0 && (
                                    <button
                                        class='btn btn-sm btn-outline-danger rounded-pill'
                                        onClick={this.clearAllDates}
                                    >
                                        <i class='fa-duotone fa-trash me-1' />
                                        <Text id='convention.clear-all'>Clear all</Text>
                                    </button>
                                )}
                            </div>

                            {conventions.length === 0 && (
                                <div class='text-muted text-center py-3 border rounded-lg mb-2'>
                                    <Text id='convention.no-dates-added'>No dates added yet. Use the repeat pattern above or add manually.</Text>
                                </div>
                            )}

                            {conventions.map((conv, index) => (
                                <div key={index} class='d-flex flex-row align-items-center mb-2 p-2 border rounded-lg'>
                                    <div class='flex-fill me-2'>
                                        <label class='text-muted small'>
                                            <Text id='convention.start-date'>Start date</Text>
                                        </label>
                                        <input
                                            class='form-control form-control-sm rounded-pill'
                                            type='datetime-local'
                                            value={conv.startDate}
                                            onInput={(e) => this.handleStartDateChange(index, e)}
                                        />
                                    </div>
                                    <div class='flex-fill me-2'>
                                        <label class='text-muted small'>
                                            <Text id='convention.end-date'>End date</Text>
                                        </label>
                                        <input
                                            class='form-control form-control-sm rounded-pill'
                                            type='datetime-local'
                                            value={conv.endDate}
                                            onInput={(e) => this.handleEndDateChange(index, e)}
                                        />
                                    </div>
                                    <div class='pt-3'>
                                        <button
                                            class='btn btn-sm btn-outline-danger rounded-pill'
                                            onClick={() => this.removeDateRow(index)}
                                            title='Remove'
                                        >
                                            <i class='fa-duotone fa-trash' />
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button
                                class='btn btn-sm btn-outline-primary rounded-pill mt-2'
                                onClick={this.addDateRow}
                            >
                                <i class='fa-duotone fa-plus me-2' />
                                <Text id='convention.add-another-date'>Add another date</Text>
                            </button>
                        </div>

                        {/* Preview */}
                        <div class={`d-flex flex-column mt-4 p-3 rounded-lg ${validCount > 0 ? 'bg-success text-white' : 'bg-warning'}`}>
                            <div class='d-flex justify-content-between align-items-center'>
                                <span class='font-weight-light'>
                                    <Text id='convention.conventions-to-create'>Conventions to create</Text>:
                                </span>
                                <span class='font-weight-bold' style='font-size: 1.5em;'>
                                    {validCount}
                                </span>
                            </div>
                        </div>

                        {error && (
                            <div class='alert alert-danger mt-3 rounded-lg'>
                                {error}
                            </div>
                        )}

                        <div class='d-flex justify-content-center mt-4'>
                            <button
                                class='btn btn-lg btn-success rounded-pill px-5'
                                onClick={this.handleGenerate}
                                disabled={loading || validCount === 0}
                            >
                                {loading ? (
                                    <>
                                        <i class='fa-duotone fa-spinner-third fa-spin me-2' />
                                        <Text id='convention.creating'>Creating...</Text>
                                    </>
                                ) : (
                                    <>
                                        <i class='fa-duotone fa-clone me-2' />
                                        <Text id='convention.create-conventions'>Create {validCount} Conventions</Text>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default BulkGenerateConventions;
